import type { CSSProperties } from "react";
import { useEffect, useState } from "react";

const read = (): CSSProperties | undefined => {
  const vv = window.visualViewport;
  return vv
    ? { top: vv.offsetTop, bottom: "auto", height: vv.height }
    : undefined;
};

/**
 * A fixed overlay's box, sized to what's visible rather than to the layout
 * viewport. Mobile browsers (iOS Safari always, Chrome by default) lay the
 * virtual keyboard over the page without shrinking it, so a sheet anchored
 * to the bottom of `inset: 0` sits behind the keyboard: while its results
 * fill it the search field stays in view near the top, but as they narrow
 * the sheet shrinks down into the keyboard and takes the field with it.
 * Spread over the backdrop's style, this keeps the sheet's bottom at the
 * keyboard's top. Undefined where there's no visualViewport (the CSS's
 * `inset: 0` stands).
 */
export function useVisualViewport(): CSSProperties | undefined {
  const [box, setBox] = useState(read);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setBox(read());
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return box;
}
